import { API_BASE_URL } from './config';
import { apiFetch, isNetworkError, markLoaded, renderError } from './helpers';
import type { MarketPricePoint, MarketsResponse } from './types';

type MarketType = 'oil' | 'sp500' | 'asia';

let marketsChartAbortController: AbortController | null = null;
let marketsSelectedDays: number = parseInt(localStorage.getItem('markets-period') || '30', 10);
const storedMarket = localStorage.getItem('markets-selected');
// Migrate legacy 'world' selection to the new 'asia' market.
let marketsSelectedMarket: MarketType = (storedMarket === 'world' ? 'asia' : (storedMarket as MarketType)) || 'oil';
if (storedMarket === 'world') localStorage.setItem('markets-selected', 'asia');

function drawMarketsChart(canvas: HTMLCanvasElement, prices: MarketPricePoint[]): void {
    if (marketsChartAbortController) marketsChartAbortController.abort();
    marketsChartAbortController = new AbortController();
    const signal = marketsChartAbortController.signal;

    const ctx = canvas.getContext('2d');
    if (!ctx || prices.length < 2) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    const w = rect.width;
    const h = rect.height;
    const padTop = 16;
    const padBottom = 24;
    const padLeft = 48;
    const padRight = 8;
    const chartW = w - padLeft - padRight;
    const chartH = h - padTop - padBottom;

    const values = prices.map(p => p.price);
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;

    const xStep = chartW / (prices.length - 1);

    ctx.strokeStyle = 'rgba(255,255,255,0.05)';
    ctx.lineWidth = 1;
    const gridLines = 4;
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    const isSP500 = marketsSelectedMarket === 'sp500';
    const isAsia = marketsSelectedMarket === 'asia';
    for (let i = 0; i <= gridLines; i++) {
        const y = padTop + (chartH / gridLines) * i;
        ctx.beginPath();
        ctx.moveTo(padLeft, y);
        ctx.lineTo(w - padRight, y);
        ctx.stroke();
        const priceAtLine = max - (range / gridLines) * i;
        ctx.fillStyle = '#5e6470';
        ctx.fillText((isSP500 || isAsia) ? priceAtLine.toFixed(0) : `$${priceAtLine.toFixed(1)}`, padLeft - 6, y);
    }

    const lineColor = isSP500 ? '#a3d977' : isAsia ? '#e8b97a' : '#8badc8';
    ctx.beginPath();
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    for (let i = 0; i < prices.length; i++) {
        const x = padLeft + i * xStep;
        const y = padTop + chartH - ((values[i] - min) / range) * chartH;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
    }
    ctx.stroke();

    const lastX = padLeft + (prices.length - 1) * xStep;
    ctx.lineTo(lastX, padTop + chartH);
    ctx.lineTo(padLeft, padTop + chartH);
    ctx.closePath();
    const gradient = ctx.createLinearGradient(0, padTop, 0, padTop + chartH);
    const fillColorStart = isSP500 ? 'rgba(163,217,119,0.18)' : isAsia ? 'rgba(232,185,122,0.18)' : 'rgba(139,173,200,0.18)';
    const fillColorEnd = isSP500 ? 'rgba(163,217,119,0)' : isAsia ? 'rgba(232,185,122,0)' : 'rgba(139,173,200,0)';
    gradient.addColorStop(0, fillColorStart);
    gradient.addColorStop(1, fillColorEnd);
    ctx.fillStyle = gradient;
    ctx.fill();

    ctx.fillStyle = '#5e6470';
    ctx.font = '10px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    const labelCount = Math.min(prices.length, 7);
    const labelStep = prices.length <= labelCount ? 1 : Math.floor(prices.length / (labelCount - 1));
    for (let i = 0; i < labelCount; i++) {
        const idx = prices.length <= labelCount ? i : Math.min(i * labelStep, prices.length - 1);
        const x = padLeft + idx * xStep;
        const datePart = prices[idx].date.split(' ')[0];
        const parts = datePart.split('-');
        ctx.fillText(`${parts[2]}.${parts[1]}`, x, h - 14);
    }

    const lastPrice = values[values.length - 1];
    const lastY = padTop + chartH - ((lastPrice - min) / range) * chartH;
    ctx.beginPath();
    ctx.arc(lastX, lastY, 3.5, 0, Math.PI * 2);
    ctx.fillStyle = lineColor;
    ctx.fill();

    const tooltip = document.getElementById('markets-tooltip');
    if (!tooltip) return;

    canvas.addEventListener('mousemove', (e: MouseEvent) => {
        const canvasRect = canvas.getBoundingClientRect();
        const mx = e.clientX - canvasRect.left;
        const idx = Math.max(0, Math.min(prices.length - 1, Math.round((mx - padLeft) / xStep)));
        const p = prices[idx];
        const px = padLeft + idx * xStep;
        const py = padTop + chartH - ((values[idx] - min) / range) * chartH;
        const dateTimeParts = p.date.split(' ');
        const dateParts = dateTimeParts[0].split('-');
        const timeStr = dateTimeParts[1] ? ` ${dateTimeParts[1]}` : '';
        const priceStr = (isSP500 || isAsia) ? p.price.toFixed(2) : `$${p.price.toFixed(2)}`;
        tooltip.textContent = `${dateParts[2]}.${dateParts[1]}.${dateParts[0]}${timeStr}  ${priceStr}`;
        tooltip.style.left = `${px}px`;
        tooltip.style.top = `${py - 32}px`;
        tooltip.style.transform = 'translateX(-50%)';
        tooltip.classList.add('visible');
    }, { signal });

    canvas.addEventListener('mouseleave', () => {
        tooltip.classList.remove('visible');
    }, { signal });
}

export async function fetchMarketsPrices(days?: number): Promise<void> {
    const container = document.getElementById('markets-data');
    const canvas = document.getElementById('markets-chart') as HTMLCanvasElement | null;
    const loading = document.getElementById('markets-loading');
    if (!container) return;

    if (days !== undefined) {
        marketsSelectedDays = days;
        localStorage.setItem('markets-period', String(days));
    }
    const queryDays = marketsSelectedDays;
    const market = marketsSelectedMarket;

    const endpoint = `markets/${market}`;

    if (loading) loading.classList.remove('hidden');

    try {
        const response = await apiFetch(`${API_BASE_URL}/${endpoint}?days=${queryDays}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data: MarketsResponse = await response.json();

        if (!data.latest) {
            container.innerHTML = '<p class="markets-error">No price data available.</p>';
            return;
        }

        const changeSign = data.change != null && data.change >= 0 ? '+' : '';
        const changeClass = data.change != null && data.change >= 0 ? 'markets-change-up' : 'markets-change-down';
        const changeHtml = data.change != null
            ? `<span class="markets-change ${changeClass}">${changeSign}${market === 'oil' ? '$' : ''}${Math.abs(data.change).toFixed(2)}</span>`
            : '';
        const pctHtml = data.changePercent != null
            ? `<span class="markets-change ${changeClass}">(${changeSign}${data.changePercent.toFixed(2)}%)</span>`
            : '';

        const periodLabel = queryDays === 1825 ? '5Y' : queryDays === 365 ? '1Y' : `${queryDays}D`;
        const label = market === 'sp500'
            ? `S&P 500 · ${periodLabel}`
            : market === 'asia'
            ? `MSCI Asia ex-Japan · USD · ${periodLabel}`
            : `Brent Crude · USD/bbl · ${periodLabel}`;
        const pricePrefix = market === 'oil' ? '$' : '$';

        container.innerHTML = `
            <span class="markets-price">${pricePrefix}${data.latest.toFixed(2)}</span>
            ${changeHtml}
            ${pctHtml}
            <span class="markets-label">${label}</span>
        `;

        if (canvas && data.prices && data.prices.length > 1) {
            canvas.style.display = '';
            drawMarketsChart(canvas, data.prices);
        }

        markLoaded(container, 'markets');
    } catch (error) {
        renderError(container, 'markets', 'Could not load market data.', error);
        if (!(isNetworkError(error) && container.dataset.loaded === 'true') && canvas) {
            canvas.style.display = 'none';
        }
    } finally {
        if (loading) loading.classList.add('hidden');
    }
}

export function initMarketsPeriodSelector(): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>('.markets-period-btn');
    buttons.forEach(btn => {
        const btnDays = parseInt(btn.dataset['days'] || '30', 10);
        btn.classList.toggle('active', btnDays === marketsSelectedDays);
    });
    buttons.forEach(btn => {
        btn.addEventListener('click', () => {
            buttons.forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            const days = parseInt(btn.dataset['days'] || '30', 10);
            fetchMarketsPrices(days);
        });
    });
}

export function initMarketsToggle(): void {
    const buttons = document.querySelectorAll<HTMLButtonElement>('.markets-toggle-btn');
    buttons.forEach(btn => {
        const btnMarket = btn.dataset['market'] as MarketType;
        btn.classList.toggle('active', btnMarket === marketsSelectedMarket);
    });
    buttons.forEach(btn => {
        btn.addEventListener('click', () => {
            buttons.forEach(b => b.classList.remove('active'));
            btn.classList.add('active');
            const market = btn.dataset['market'] as MarketType;
            marketsSelectedMarket = market;
            localStorage.setItem('markets-selected', market);
            fetchMarketsPrices();
        });
    });
}
